import React, { useState, useRef, useEffect } from 'react';
import { 
  Bold, Italic, Heading1, Heading2, Heading3, List, Link as LinkIcon, 
  Code, Eye, EyeOff, Quote, Palette, Type, MousePointerClick, Columns, GripHorizontal,
  Undo, Redo
} from 'lucide-react';
import { useLanguage } from '../../services/languageContext';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: string;
}

// --- EXTENDED COLORS PALETTE ---
const COLORS = [
  { name: 'red', hex: '#ef4444', label: 'Red' },
  { name: 'orange', hex: '#f97316', label: 'Orange' },
  { name: 'amber', hex: '#f59e0b', label: 'Amber' },
  { name: 'yellow', hex: '#eab308', label: 'Yellow' },
  { name: 'lime', hex: '#84cc16', label: 'Lime' },
  { name: 'green', hex: '#22c55e', label: 'Green' },
  { name: 'emerald', hex: '#10b981', label: 'Emerald' },
  { name: 'teal', hex: '#14b8a6', label: 'Teal' },
  { name: 'cyan', hex: '#06b6d4', label: 'Cyan' },
  { name: 'sky', hex: '#0ea5e9', label: 'Sky' },
  { name: 'blue', hex: '#3b82f6', label: 'Blue' },
  { name: 'indigo', hex: '#6366f1', label: 'Indigo' },
  { name: 'violet', hex: '#8b5cf6', label: 'Violet' },
  { name: 'purple', hex: '#a855f7', label: 'Purple' },
  { name: 'fuchsia', hex: '#d946ef', label: 'Fuchsia' },
  { name: 'pink', hex: '#ec4899', label: 'Pink' },
  { name: 'rose', hex: '#f43f5e', label: 'Rose' },
  { name: 'white', hex: '#ffffff', label: 'White' },
  { name: 'gray', hex: '#9ca3af', label: 'Gray' },
];

// --- RECURSIVE RENDERER COMPONENT (Fixes Stacking) ---
const RecursiveParser: React.FC<{ text: string }> = ({ text }) => {
  if (!text) return null;

  const rules = [
    { pattern: /^\*\*(.*?)\*\*/, render: (c: string, _?: string) => <strong className="font-bold text-white normal-case"><RecursiveParser text={c}/></strong> }, 
    { pattern: /^\*(.*?)\*/, render: (c: string, _?: string) => <em className="italic text-indigo-200 normal-case"><RecursiveParser text={c}/></em> }, 
    { pattern: /^`(.*?)`/, render: (c: string, _?: string) => <code className="bg-white/10 px-1.5 py-0.5 rounded text-xs font-mono text-cyan-300 mx-1">{c}</code> }, 
    { pattern: /^\[(.*?)\]\((.*?)\)/, render: (c: string, url?: string) => <a href={url} target="_blank" rel="noopener" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-4 decoration-indigo-500/30 transition-colors"><RecursiveParser text={c}/></a> }, 
    { pattern: /^\{color:(.*?)\}(.*?){\/color}/, render: (c: string, col?: string) => {
        const colorObj = COLORS.find(cl => cl.name === col);
        const style = colorObj ? { color: colorObj.hex } : {};
        return <span style={style} className="font-medium normal-case"><RecursiveParser text={c}/></span>;
    }} 
  ];

  const elements: React.ReactNode[] = [];
  let remaining = text;

  while (remaining.length > 0) {
    let matchFound = false;
    for (const rule of rules) {
      const match = remaining.match(rule.pattern);
      if (match && match.index === 0) {
        if (rule.pattern.source.startsWith('^\\[')) {
           elements.push(<React.Fragment key={elements.length}>{rule.render(match[1], match[2])}</React.Fragment>);
        } else if (rule.pattern.source.startsWith('^\\{color')) {
           elements.push(<React.Fragment key={elements.length}>{rule.render(match[2], match[1])}</React.Fragment>);
        } else {
           elements.push(<React.Fragment key={elements.length}>{rule.render(match[1])}</React.Fragment>);
        }
        remaining = remaining.slice(match[0].length);
        matchFound = true;
        break;
      }
    }

    if (!matchFound) {
      const nextSpecial = remaining.search(/[\*`\[\{]/);
      if (nextSpecial === -1) {
        elements.push(remaining);
        remaining = '';
      } else if (nextSpecial === 0) {
        elements.push(remaining[0]);
        remaining = remaining.slice(1);
      } else {
        elements.push(remaining.slice(0, nextSpecial));
        remaining = remaining.slice(nextSpecial);
      }
    }
  }

  return <>{elements}</>;
};

export const MarkdownRenderer: React.FC<{ content: string, className?: string }> = ({ content, className = '' }) => {
  if (!content) return null;
  const lines = content.split('\n');

  return (
    <div className={`space-y-2 leading-relaxed ${className}`}>
      {lines.map((line, idx) => {
        if (line.startsWith('# ')) return <h1 key={idx} className="text-2xl font-display font-black text-white mt-6 mb-3 uppercase tracking-wide border-b border-white/10 pb-2 normal-case"><RecursiveParser text={line.slice(2)} /></h1>;
        if (line.startsWith('## ')) return <h2 key={idx} className="text-xl font-display font-bold text-white mt-5 mb-2 uppercase tracking-wide normal-case"><RecursiveParser text={line.slice(3)} /></h2>;
        if (line.startsWith('### ')) return <h3 key={idx} className="text-lg font-bold text-indigo-300 mt-4 mb-1 uppercase tracking-widest normal-case"><RecursiveParser text={line.slice(4)} /></h3>;
        
        if (line.startsWith('- ')) return <li key={idx} className="ml-4 list-disc marker:text-indigo-500 pl-2 text-sm text-gray-300"><RecursiveParser text={line.slice(2)} /></li>;
        if (line.startsWith('> ')) return <blockquote key={idx} className="border-l-4 border-indigo-500 pl-4 italic text-gray-400 my-3 bg-white/[0.02] py-2 rounded-r"><RecursiveParser text={line.slice(2)} /></blockquote>;
        if (line.trim() === '---') return <div key={idx} className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent my-6" />;
        if (line.trim() === '') return <br key={idx} />;

        return <p key={idx} className="text-sm font-light text-gray-300"><RecursiveParser text={line} /></p>;
      })}
    </div>
  );
};

// --- EDITOR COMPONENT ---

type EditorMode = 'edit' | 'preview' | 'split';

export const MarkdownEditor: React.FC<MarkdownEditorProps> = ({ value, onChange, placeholder, height = "h-96" }) => {
  const { t } = useLanguage();
  const [mode, setMode] = useState<EditorMode>('split'); 
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; visible: boolean }>({ x: 0, y: 0, visible: false });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // --- HISTORY STATE ---
  const historyRef = useRef<string[]>([value]);
  const historyIndexRef = useRef(0);
  const typingTimeoutRef = useRef<any>(null);

  useEffect(() => {
    // Sync initial value if provided and history is empty (safety check)
    if (historyRef.current.length === 0) {
        historyRef.current = [value];
    }
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
            setContextMenu(prev => ({ ...prev, visible: false }));
        }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // --- HISTORY MANAGEMENT ---
  const pushToHistory = (newValue: string) => {
    // If we are somewhere back in history, remove future
    if (historyIndexRef.current < historyRef.current.length - 1) {
      historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
    }
    
    // Avoid duplicates
    if (newValue !== historyRef.current[historyIndexRef.current]) {
        historyRef.current.push(newValue);
        historyIndexRef.current = historyRef.current.length - 1;
        // Optional: Cap history size to 50
        if (historyRef.current.length > 50) {
            historyRef.current.shift();
            historyIndexRef.current--;
        }
    }
  };

  const handleUndo = () => {
    if (historyIndexRef.current > 0) {
      historyIndexRef.current -= 1;
      const prevValue = historyRef.current[historyIndexRef.current];
      onChange(prevValue);
    }
  };

  const handleRedo = () => {
    if (historyIndexRef.current < historyRef.current.length - 1) {
      historyIndexRef.current += 1;
      const nextValue = historyRef.current[historyIndexRef.current];
      onChange(nextValue);
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    onChange(val);

    // Debounce history save for typing to group characters
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
        pushToHistory(val);
    }, 700); // 700ms pause = save point
  };

  // Keyboard Shortcuts
  const handleKeyDown = (e: React.KeyboardEvent) => {
      // Undo / Redo
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
          e.preventDefault();
          if (e.shiftKey) {
              handleRedo();
          } else {
              handleUndo();
          }
          return;
      }
      // Redo alternative (Ctrl+Y)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
          e.preventDefault();
          handleRedo();
          return;
      }

      if (e.ctrlKey || e.metaKey) {
          switch(e.key.toLowerCase()) {
              case 'b': e.preventDefault(); toggleSyntax('**', '**'); break;
              case 'i': e.preventDefault(); toggleSyntax('*', '*'); break;
          }
      }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
      e.preventDefault();
      setContextMenu({
          x: e.clientX,
          y: e.clientY,
          visible: true
      });
  };

  const toggleSyntax = (prefix: string, suffix: string = '') => {
    if (!textareaRef.current) return;
    
    // Force save any pending typed text before applying tool
    if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        pushToHistory(value);
    }

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = value;
    
    const before = text.substring(0, start);
    const selection = text.substring(start, end);
    const after = text.substring(end);

    let newText = '';
    let newSelectionStart = start;
    let newSelectionEnd = end;

    // Smart Toggle Logic
    if (selection.startsWith(prefix) && selection.endsWith(suffix)) {
        const unwrapped = selection.substring(prefix.length, selection.length - suffix.length);
        newText = before + unwrapped + after;
        newSelectionEnd = start + unwrapped.length;
    } else if (before.endsWith(prefix) && after.startsWith(suffix)) {
        newText = before.substring(0, before.length - prefix.length) + selection + after.substring(suffix.length);
        newSelectionStart = start - prefix.length;
        newSelectionEnd = end - prefix.length;
    } else {
        newText = before + prefix + selection + suffix + after;
        newSelectionStart = start + prefix.length;
        newSelectionEnd = end + prefix.length;
    }

    onChange(newText);
    pushToHistory(newText); // Save tool action immediately

    setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(newSelectionStart, newSelectionEnd);
    }, 0);
    setContextMenu(prev => ({ ...prev, visible: false }));
  };

  const insertBlock = (prefix: string) => {
      toggleSyntax(prefix);
  };

  return (
    <div className="w-full border border-white/10 rounded-xl overflow-hidden bg-[#0F0F12] group focus-within:border-indigo-500/50 transition-colors flex flex-col relative">
      
      {/* TOOLBAR */}
      <div className="flex items-center justify-between p-2 border-b border-white/10 bg-[#151515] select-none">
         <div className="flex items-center gap-1 overflow-x-auto hide-scrollbar scroll-smooth">
            
            {/* History Controls */}
            <div className="flex gap-0.5 bg-[#0A0A0A] p-1 rounded-lg border border-white/5 mr-2">
                <ToolbarBtn onClick={handleUndo} icon={Undo} title="Undo (Ctrl+Z)" />
                <ToolbarBtn onClick={handleRedo} icon={Redo} title="Redo (Ctrl+Shift+Z)" />
            </div>

            <div className="w-px h-6 bg-white/10 mx-1" />

            <div className="flex gap-0.5 bg-[#0A0A0A] p-1 rounded-lg border border-white/5">
                <ToolbarBtn onClick={() => toggleSyntax('**', '**')} icon={Bold} title={t('md_bold')} />
                <ToolbarBtn onClick={() => toggleSyntax('*', '*')} icon={Italic} title={t('md_italic')} />
                <ToolbarBtn onClick={() => toggleSyntax('`', '`')} icon={Code} title={t('md_code')} />
            </div>
            
            <div className="w-px h-6 bg-white/10 mx-1" />
            
            <div className="flex gap-0.5 bg-[#0A0A0A] p-1 rounded-lg border border-white/5">
                <ToolbarBtn onClick={() => insertBlock('# ')} icon={Heading1} title="H1" />
                <ToolbarBtn onClick={() => insertBlock('## ')} icon={Heading2} title="H2" />
                <ToolbarBtn onClick={() => insertBlock('### ')} icon={Heading3} title="H3" />
            </div>

            <div className="w-px h-6 bg-white/10 mx-1" />

            <div className="flex gap-0.5 bg-[#0A0A0A] p-1 rounded-lg border border-white/5">
                <ToolbarBtn onClick={() => insertBlock('- ')} icon={List} title={t('md_list')} />
                <ToolbarBtn onClick={() => insertBlock('> ')} icon={Quote} title={t('md_quote')} />
                <ToolbarBtn onClick={() => toggleSyntax('[Link](', ')')} icon={LinkIcon} title={t('md_link')} />
            </div>
            
            <div className="w-px h-6 bg-white/10 mx-1" />

            {/* Quick Colors */}
            <div className="flex gap-1.5 ml-1 items-center px-2">
                <button type="button" onClick={() => toggleSyntax('{color:red}', '{/color}')} className="w-4 h-4 rounded-full bg-red-500 hover:scale-125 transition-transform border border-white/10" title="Red" />
                <button type="button" onClick={() => toggleSyntax('{color:green}', '{/color}')} className="w-4 h-4 rounded-full bg-green-500 hover:scale-125 transition-transform border border-white/10" title="Green" />
                <button type="button" onClick={() => toggleSyntax('{color:blue}', '{/color}')} className="w-4 h-4 rounded-full bg-blue-500 hover:scale-125 transition-transform border border-white/10" title="Blue" />
                
                <div className="relative group/palette ml-1">
                    <Palette size={14} className="text-gray-500 group-hover/palette:text-white cursor-help" />
                    <div className="absolute top-full left-0 mt-2 p-3 bg-[#1a1a1a] border border-white/10 rounded-xl shadow-xl opacity-0 invisible group-hover/palette:opacity-100 group-hover/palette:visible transition-all z-50 w-48 grid grid-cols-5 gap-2">
                        {COLORS.map(c => (
                            <button
                                key={c.name}
                                type="button"
                                onClick={() => toggleSyntax(`{color:${c.name}}`, '{/color}')}
                                className="w-6 h-6 rounded-full hover:scale-110 transition-transform border border-white/10"
                                style={{ backgroundColor: c.hex }}
                                title={c.label}
                            />
                        ))}
                    </div>
                </div>
            </div>
         </div>

         <div className="flex items-center pl-2 border-l border-white/10 bg-[#050505] rounded p-1">
            <button onClick={() => setMode('edit')} className={`p-1.5 rounded transition-colors ${mode==='edit' ? 'bg-white/20 text-white' : 'text-gray-500'}`} title={t('md_edit')}><Code size={14}/></button>
            <button onClick={() => setMode('split')} className={`p-1.5 rounded transition-colors ${mode==='split' ? 'bg-white/20 text-white' : 'text-gray-500'}`} title={t('md_split')}><Columns size={14}/></button>
            <button onClick={() => setMode('preview')} className={`p-1.5 rounded transition-colors ${mode==='preview' ? 'bg-white/20 text-white' : 'text-gray-500'}`} title={t('md_preview')}><Eye size={14}/></button>
         </div>
      </div>

      {/* EDIT AREA - RESIZABLE CONTAINER */}
      <div 
        className={`relative ${height} min-h-[300px] bg-[#121212] flex flex-col resize-y overflow-hidden`}
        style={{ resize: 'vertical' }}
      >
         <div className="flex-1 flex overflow-hidden">
             
             {/* LEFT: Editor */}
             {(mode === 'edit' || mode === 'split') && (
                <div className={`h-full ${mode === 'split' ? 'w-1/2 border-r border-white/10' : 'w-full'} relative`}>
                    <textarea
                      ref={textareaRef}
                      value={value}
                      onChange={handleTextChange}
                      onKeyDown={handleKeyDown}
                      onContextMenu={handleContextMenu}
                      className="w-full h-full bg-transparent p-6 text-sm font-mono text-gray-300 focus:outline-none resize-none placeholder:text-gray-700 leading-relaxed selection:bg-indigo-500/30 selection:text-white custom-scrollbar"
                      placeholder={placeholder || t('md_placeholder')}
                      spellCheck={false}
                    />
                </div>
             )}

             {/* RIGHT: Preview */}
             {(mode === 'preview' || mode === 'split') && (
                <div className={`h-full ${mode === 'split' ? 'w-1/2' : 'w-full'} overflow-y-auto custom-scrollbar bg-[#0A0A0A] p-6`}>
                   <MarkdownRenderer content={value} />
                </div>
             )}
         </div>
         
         <div className="absolute bottom-0 right-0 p-1 pointer-events-none text-white/20 bg-gradient-to-tl from-black to-transparent rounded-tl-lg">
            <GripHorizontal size={14} />
         </div>
      </div>
      
      {/* STATUS BAR */}
      <div className="px-4 py-2 bg-[#080808] border-t border-white/5 text-[10px] text-gray-600 flex justify-between font-mono">
         <span className="flex items-center gap-2"><MousePointerClick size={10}/> RMB for menu</span>
         <span>{value.length} {t('md_chars')}</span>
      </div>

      {/* --- CUSTOM CONTEXT MENU --- */}
      {contextMenu.visible && (
          <div 
            ref={menuRef}
            className="fixed z-[9999] w-56 bg-[#1a1a1a]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.5)] overflow-hidden animate-enter-up p-1.5"
            style={{ top: contextMenu.y, left: contextMenu.x }}
          >
             <div className="text-[9px] font-bold text-gray-500 uppercase tracking-widest px-3 py-2 border-b border-white/5 mb-1">
                {t('md_ctx_format')}
             </div>
             
             <ContextMenuItem onClick={() => toggleSyntax('**', '**')} icon={Bold} label={t('md_bold')} shortcut="Ctrl+B" />
             <ContextMenuItem onClick={() => toggleSyntax('*', '*')} icon={Italic} label={t('md_italic')} shortcut="Ctrl+I" />
             <ContextMenuItem onClick={() => toggleSyntax('`', '`')} icon={Code} label={t('md_code')} />
             
             <div className="h-px bg-white/5 my-1" />
             
             <div className="text-[9px] font-bold text-gray-500 uppercase tracking-widest px-3 py-2 border-b border-white/5 mb-1 mt-1">
                {t('md_ctx_headers')}
             </div>
             <ContextMenuItem onClick={() => insertBlock('## ')} icon={Heading2} label="Header Large" />
             <ContextMenuItem onClick={() => insertBlock('### ')} icon={Heading3} label="Header Small" />
             <ContextMenuItem onClick={() => insertBlock('- ')} icon={List} label={t('md_list')} />
             
             <div className="h-px bg-white/5 my-1" />
             
             <div className="px-3 py-1">
                <p className="text-[9px] font-bold text-gray-500 uppercase tracking-widest mb-2">{t('md_ctx_color')}</p>
                <div className="grid grid-cols-6 gap-1.5">
                    {COLORS.slice(0, 12).map(c => (
                        <button
                            key={c.name}
                            type="button"
                            onClick={() => toggleSyntax(`{color:${c.name}}`, '{/color}')}
                            className="w-5 h-5 rounded-full hover:scale-125 transition-transform border border-white/10 hover:border-white shadow-sm"
                            style={{ backgroundColor: c.hex }}
                            title={c.label}
                        />
                    ))}
                </div>
             </div>
          </div>
      )}
    </div>
  );
};

const ToolbarBtn = ({ onClick, icon: Icon, title }: any) => (
  <button 
    onClick={onClick}
    className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-md transition-colors relative group"
    title={title}
    type="button"
  >
    <Icon size={15} strokeWidth={2} />
  </button>
);

const ContextMenuItem = ({ onClick, icon: Icon, label, shortcut }: any) => (
    <button 
        onClick={onClick}
        className="w-full text-left px-3 py-2 rounded-lg hover:bg-indigo-600 hover:text-white text-gray-300 text-xs font-medium flex items-center justify-between transition-colors group"
    >
        <div className="flex items-center gap-2">
            <Icon size={14} className="text-gray-500 group-hover:text-indigo-200" />
            <span>{label}</span>
        </div>
        {shortcut && <span className="text-[9px] opacity-50 font-mono">{shortcut}</span>}
    </button>
);